/** @module @liquidefy/code-editor/circuits/ai-code-review @version 1.0.0 @license LQD Research LLC */
/** @rust ai_code_review.rs — false; 4/2/26; sm */

/**
 * Tier 4 — Better Than Existing: AI-Powered Code Review
 *
 * Combines diff computation, AI-driven analysis, security scanning,
 * and human review into a unified code review circuit.
 * Produces auto-comments, security findings, quality scores,
 * and risk assessments from a diff of original vs modified code.
 *
 * Pure circuit. No DOM, no framework coupling.
 */

// ═══════════════════════════════════════════════════════════════════════════════
// §1 IMPORTS
// ═══════════════════════════════════════════════════════════════════════════════

import type { CircuitHandle, CircuitHealthReport, CircuitStats } from './circuit-types.js';
import { buildHealthReport, emptyStats, recordSuccess, recordError, healthFromStats, formatStatsDetails } from './circuit-types.js';

import type {
  DiffResult,
  DiffHunk,
  EditorResult,
  Range,
} from '../code-editor-types.js';

import { ok, err } from '../code-editor.js';
import { computeDiff } from '../diff/compute.js';
import { buildAnalysisRequest, securityAnalysis, quickAnalysis } from '../ai/agents/analysis.js';
import { assessSecurity } from '../ai/agents/generation.js';
import { INJECTION_PATTERNS, DATA_EXTRACTION_PATTERNS } from '../ai/safety/security.js';

// ═══════════════════════════════════════════════════════════════════════════════
// §2 TYPES
// ═══════════════════════════════════════════════════════════════════════════════

export type ReviewFocus = 'security' | 'quality' | 'performance' | 'style';

export interface AICodeReviewConfig {
  readonly originalContent: string;
  readonly modifiedContent: string;
  readonly language?: string;
  readonly reviewFocus?: readonly ReviewFocus[];
}

export interface AutoComment {
  readonly line: number;
  readonly severity: 'error' | 'warning' | 'info' | 'suggestion';
  readonly message: string;
  readonly suggestion?: string;
  readonly category: ReviewFocus;
}

export interface SecurityFinding {
  readonly line: number;
  readonly type: string;
  readonly severity: 'critical' | 'high' | 'medium' | 'low';
  readonly cwe?: string;
  readonly description: string;
}

export interface QualityScore {
  readonly overall: number;
  readonly complexity: number;
  readonly maintainability: number;
}

export interface RiskAssessment {
  readonly level: 'low' | 'medium' | 'high' | 'critical';
  readonly reason: string;
  readonly factors: string[];
}

export interface ReviewComment {
  readonly line: number;
  readonly source: 'ai' | 'human';
  readonly body: string;
  readonly severity?: string;
}

export interface ReviewSummary {
  readonly linesChanged: number;
  readonly autoComments: number;
  readonly securityIssues: number;
  readonly qualityScore: number;
  readonly riskLevel: string;
}

export interface AICodeReviewHandle extends CircuitHandle {
  getDiff(): DiffResult;
  getAutoComments(): AutoComment[];
  getSecurityFindings(): SecurityFinding[];
  getQualityScore(): QualityScore;
  getRiskAssessment(): RiskAssessment;
  addHumanComment(line: number, body: string): void;
  getAllComments(): ReviewComment[];
  approve(): void;
  requestChanges(reason: string): void;
  getSummary(): ReviewSummary;
  stats(): CircuitStats;
  health(): CircuitHealthReport;
  destroy(): void;
}

// ═══════════════════════════════════════════════════════════════════════════════
// §3 SECURITY PATTERN DATABASE
// ═══════════════════════════════════════════════════════════════════════════════

interface SecurityPattern {
  readonly pattern: RegExp;
  readonly type: string;
  readonly severity: 'critical' | 'high' | 'medium' | 'low';
  readonly cwe?: string;
  readonly description: string;
}

const SECURITY_PATTERNS: SecurityPattern[] = [
  { pattern: /eval\s*\(/, type: 'code-injection', severity: 'critical', cwe: 'CWE-94', description: 'Use of eval() allows arbitrary code execution' },
  { pattern: /innerHTML\s*=/, type: 'xss', severity: 'high', cwe: 'CWE-79', description: 'Direct innerHTML assignment is vulnerable to XSS' },
  { pattern: /document\.write\s*\(/, type: 'xss', severity: 'high', cwe: 'CWE-79', description: 'document.write can introduce XSS vulnerabilities' },
  { pattern: /new\s+Function\s*\(/, type: 'code-injection', severity: 'high', cwe: 'CWE-94', description: 'new Function() is equivalent to eval()' },
  { pattern: /\bexec\s*\(/, type: 'command-injection', severity: 'critical', cwe: 'CWE-78', description: 'exec() can allow OS command injection' },
  { pattern: /child_process/, type: 'command-injection', severity: 'high', cwe: 'CWE-78', description: 'child_process usage may allow command injection' },
  { pattern: /\.env\b.*(?:password|secret|key|token)/i, type: 'secrets-exposure', severity: 'high', cwe: 'CWE-798', description: 'Potential hardcoded credentials' },
  { pattern: /(?:password|secret|api_key|token)\s*[:=]\s*['"][^'"]+['"]/i, type: 'secrets-exposure', severity: 'critical', cwe: 'CWE-798', description: 'Hardcoded secret detected' },
  { pattern: /Math\.random\(\)/, type: 'weak-crypto', severity: 'medium', cwe: 'CWE-338', description: 'Math.random() is not cryptographically secure' },
  { pattern: /http:\/\/(?!localhost)/, type: 'insecure-transport', severity: 'medium', cwe: 'CWE-319', description: 'HTTP used instead of HTTPS' },
  { pattern: /SELECT\s+.*\+.*(?:req|input|param|query)/i, type: 'sql-injection', severity: 'critical', cwe: 'CWE-89', description: 'Potential SQL injection via string concatenation' },
  { pattern: /dangerouslySetInnerHTML/, type: 'xss', severity: 'medium', cwe: 'CWE-79', description: 'dangerouslySetInnerHTML can introduce XSS' },
  { pattern: /(?:disable|skip|ignore).*(?:ssl|tls|cert)/i, type: 'insecure-config', severity: 'high', cwe: 'CWE-295', description: 'SSL/TLS verification disabled' },
  { pattern: /cors.*\*/, type: 'misconfiguration', severity: 'medium', cwe: 'CWE-942', description: 'Wildcard CORS configuration' },
  { pattern: /TODO.*(?:security|auth|fix)/i, type: 'incomplete-security', severity: 'low', description: 'Security-related TODO found' },
];

// ═══════════════════════════════════════════════════════════════════════════════
// §4 QUALITY ANALYSIS ENGINE
// ═══════════════════════════════════════════════════════════════════════════════

function computeComplexity(content: string): number {
  const lines = content.split('\n');
  let cyclomatic = 1;
  for (const line of lines) {
    const trimmed = line.trim();
    if (/\b(if|else\s+if|for|while|switch|case|catch|\?\?|&&|\|\||\?)\b/.test(trimmed)) {
      cyclomatic++;
    }
  }
  // Normalize: 1-10 = 100, 11-20 = 80-50, 21+ = below 50
  if (cyclomatic <= 10) return 100;
  if (cyclomatic <= 20) return Math.max(50, 100 - (cyclomatic - 10) * 5);
  return Math.max(10, 50 - (cyclomatic - 20) * 2);
}

function computeMaintainability(content: string): number {
  const lines = content.split('\n');
  const totalLines = lines.length;
  let commentLines = 0;
  let blankLines = 0;
  let maxLineLength = 0;
  let longLines = 0;

  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed) blankLines++;
    else if (trimmed.startsWith('//') || trimmed.startsWith('/*') || trimmed.startsWith('*')) commentLines++;
    if (line.length > maxLineLength) maxLineLength = line.length;
    if (line.length > 120) longLines++;
  }

  let score = 100;
  const commentRatio = totalLines > 0 ? commentLines / totalLines : 0;
  if (commentRatio < 0.05 && totalLines > 20) score -= 15;
  if (longLines > totalLines * 0.1) score -= 10;
  if (totalLines > 500) score -= Math.min(20, Math.floor((totalLines - 500) / 100) * 5);

  return Math.max(0, Math.min(100, score));
}

// ═══════════════════════════════════════════════════════════════════════════════
// §5 AUTO-COMMENT GENERATION
// ═══════════════════════════════════════════════════════════════════════════════

function generateAutoComments(diff: DiffResult, language: string, focus: readonly ReviewFocus[]): AutoComment[] {
  const comments: AutoComment[] = [];
  const addedLines = extractAddedLines(diff);

  for (const { lineNumber, content } of addedLines) {
    // Style checks
    if (focus.includes('style')) {
      if (content.length > 120) {
        comments.push({ line: lineNumber, severity: 'suggestion', message: `Line is ${content.length} chars long; consider wrapping`, category: 'style' });
      }
      if (/\t/.test(content) && / {2,}/.test(content)) {
        comments.push({ line: lineNumber, severity: 'info', message: 'Mixed tabs and spaces detected', category: 'style' });
      }
      if (/\bvar\b/.test(content) && (language === 'typescript' || language === 'javascript')) {
        comments.push({ line: lineNumber, severity: 'suggestion', message: 'Prefer const/let over var', suggestion: content.replace(/\bvar\b/, 'const'), category: 'style' });
      }
      if (/\bany\b/.test(content) && language === 'typescript') {
        comments.push({ line: lineNumber, severity: 'warning', message: 'Avoid using `any` — provide a specific type', category: 'style' });
      }
    }

    // Quality checks
    if (focus.includes('quality')) {
      if (/console\.(log|debug|info)\b/.test(content)) {
        comments.push({ line: lineNumber, severity: 'warning', message: 'Remove console output before merging', category: 'quality' });
      }
      if (/\/\/\s*@ts-ignore/.test(content)) {
        comments.push({ line: lineNumber, severity: 'warning', message: '@ts-ignore suppresses type checking — consider fixing the underlying issue', category: 'quality' });
      }
      if (/catch\s*\(\s*\w*\s*\)\s*\{\s*\}/.test(content)) {
        comments.push({ line: lineNumber, severity: 'error', message: 'Empty catch block swallows errors', suggestion: 'Log or re-throw the error', category: 'quality' });
      }
    }

    // Performance checks
    if (focus.includes('performance')) {
      if (/\.forEach\(/.test(content) && /\.filter\(/.test(content)) {
        comments.push({ line: lineNumber, severity: 'suggestion', message: 'Chained filter + forEach could be a single reduce/for-of', category: 'performance' });
      }
      if (/JSON\.parse\(JSON\.stringify\(/.test(content)) {
        comments.push({ line: lineNumber, severity: 'warning', message: 'JSON.parse(JSON.stringify(...)) is slow for deep cloning — use structuredClone', category: 'performance' });
      }
      if (/new RegExp\(/.test(content) && !/\bconst\b/.test(content)) {
        comments.push({ line: lineNumber, severity: 'suggestion', message: 'Hoist RegExp creation outside loops for better performance', category: 'performance' });
      }
    }
  }

  return comments;
}

function extractAddedLines(diff: DiffResult): Array<{ lineNumber: number; content: string }> {
  const added: Array<{ lineNumber: number; content: string }> = [];
  for (const hunk of diff.hunks) {
    for (const line of hunk.lines) {
      if (line.type === 'add' && line.newLineNumber !== undefined) {
        added.push({ lineNumber: line.newLineNumber, content: line.content });
      }
    }
  }
  return added;
}

// ═══════════════════════════════════════════════════════════════════════════════
// §6 SECURITY SCANNER
// ═══════════════════════════════════════════════════════════════════════════════

function scanForSecurity(diff: DiffResult): SecurityFinding[] {
  const findings: SecurityFinding[] = [];
  const addedLines = extractAddedLines(diff);

  for (const { lineNumber, content } of addedLines) {
    for (const pattern of SECURITY_PATTERNS) {
      if (pattern.pattern.test(content)) {
        findings.push({
          line: lineNumber,
          type: pattern.type,
          severity: pattern.severity,
          cwe: pattern.cwe,
          description: pattern.description,
        });
      }
    }
  }

  return findings;
}

// ═══════════════════════════════════════════════════════════════════════════════
// §7 RISK ASSESSMENT ENGINE
// ═══════════════════════════════════════════════════════════════════════════════

function assessRisk(diff: DiffResult, securityFindings: SecurityFinding[], qualityScore: QualityScore): RiskAssessment {
  const factors: string[] = [];
  let riskScore = 0;

  // Volume of change
  const totalChanged = diff.stats.additions + diff.stats.deletions;
  if (totalChanged > 500) {
    riskScore += 3;
    factors.push(`Large change: ${totalChanged} lines modified`);
  } else if (totalChanged > 200) {
    riskScore += 2;
    factors.push(`Moderate change: ${totalChanged} lines modified`);
  } else if (totalChanged > 50) {
    riskScore += 1;
    factors.push(`${totalChanged} lines modified`);
  }

  // Security findings
  const criticalSecurity = securityFindings.filter(f => f.severity === 'critical').length;
  const highSecurity = securityFindings.filter(f => f.severity === 'high').length;
  if (criticalSecurity > 0) {
    riskScore += 4;
    factors.push(`${criticalSecurity} critical security issue(s)`);
  }
  if (highSecurity > 0) {
    riskScore += 2;
    factors.push(`${highSecurity} high-severity security issue(s)`);
  }

  // Quality concerns
  if (qualityScore.overall < 50) {
    riskScore += 2;
    factors.push('Low overall quality score');
  }
  if (qualityScore.complexity < 40) {
    riskScore += 2;
    factors.push('High cyclomatic complexity');
  }

  // Deletions outweigh additions (refactor risk)
  if (diff.stats.deletions > diff.stats.additions * 2 && diff.stats.deletions > 50) {
    riskScore += 1;
    factors.push('Heavy deletions — verify no functionality lost');
  }

  let level: RiskAssessment['level'];
  if (riskScore >= 6) level = 'critical';
  else if (riskScore >= 4) level = 'high';
  else if (riskScore >= 2) level = 'medium';
  else level = 'low';

  const reason = factors.length > 0 ? factors.join('; ') : 'No significant risk factors identified';
  return { level, reason, factors };
}

// ═══════════════════════════════════════════════════════════════════════════════
// §8 FACTORY — createAICodeReview
// ═══════════════════════════════════════════════════════════════════════════════

export function createAICodeReview(config: AICodeReviewConfig): AICodeReviewHandle {
  let circuitStats: CircuitStats = emptyStats();
  let destroyed = false;
  const createdAt = Date.now();

  function guardDestroyed(): void {
    if (destroyed) throw new Error('Circuit destroyed');
  }

  const language = config.language ?? 'typescript';
  const focus: readonly ReviewFocus[] = config.reviewFocus ?? ['security', 'quality', 'performance', 'style'];

  // ── Compute diff ──
  const diffResult = computeDiff(config.originalContent, config.modifiedContent);
  if (!diffResult.success) throw new Error(`Diff computation failed: ${diffResult.code}`);
  const diff: DiffResult = diffResult.value;

  // ── Pre-compute analyses ──
  const autoComments = generateAutoComments(diff, language, focus);
  const securityFindings = scanForSecurity(diff);
  const complexityScore = computeComplexity(config.modifiedContent);
  const maintainabilityScore = computeMaintainability(config.modifiedContent);
  const qualityScore: QualityScore = {
    overall: Math.round((complexityScore + maintainabilityScore) / 2),
    complexity: complexityScore,
    maintainability: maintainabilityScore,
  };
  const riskAssessment = assessRisk(diff, securityFindings, qualityScore);

  // ── Mutable state ──
  const humanComments: ReviewComment[] = [];
  let approvalState: 'pending' | 'approved' | 'changes-requested' = 'pending';
  let changesReason: string | undefined;

  // ── Methods ──

  function getDiff(): DiffResult {
    guardDestroyed();
    circuitStats = recordSuccess(circuitStats);
    return diff;
  }

  function getAutoComments(): AutoComment[] {
    guardDestroyed();
    circuitStats = recordSuccess(circuitStats);
    return [...autoComments];
  }

  function getSecurityFindings(): SecurityFinding[] {
    guardDestroyed();
    circuitStats = recordSuccess(circuitStats);
    return [...securityFindings];
  }

  function getQualityScore(): QualityScore {
    guardDestroyed();
    circuitStats = recordSuccess(circuitStats);
    return qualityScore;
  }

  function getRiskAssessment(): RiskAssessment {
    guardDestroyed();
    circuitStats = recordSuccess(circuitStats);
    return riskAssessment;
  }

  function addHumanComment(line: number, body: string): void {
    guardDestroyed();
    humanComments.push({ line, source: 'human', body });
    circuitStats = recordSuccess(circuitStats);
  }

  function getAllComments(): ReviewComment[] {
    guardDestroyed();
    const aiComments: ReviewComment[] = autoComments.map(c => ({
      line: c.line,
      source: 'ai' as const,
      body: c.message,
      severity: c.severity,
    }));
    circuitStats = recordSuccess(circuitStats);
    return [...aiComments, ...humanComments].sort((a, b) => a.line - b.line);
  }

  function approve(): void {
    guardDestroyed();
    approvalState = 'approved';
    circuitStats = recordSuccess(circuitStats);
  }

  function requestChanges(reason: string): void {
    guardDestroyed();
    approvalState = 'changes-requested';
    changesReason = reason;
    circuitStats = recordSuccess(circuitStats);
  }

  function getSummary(): ReviewSummary {
    guardDestroyed();
    circuitStats = recordSuccess(circuitStats);
    return {
      linesChanged: diff.stats.additions + diff.stats.deletions,
      autoComments: autoComments.length,
      securityIssues: securityFindings.length,
      qualityScore: qualityScore.overall,
      riskLevel: riskAssessment.level,
    };
  }

  function getCircuitStats(): CircuitStats {
    return { ...circuitStats, uptime: Date.now() - createdAt };
  }

  function getHealth(): CircuitHealthReport {
    const h = destroyed ? 'destroyed' as const : healthFromStats(circuitStats);
    return buildHealthReport('ai-code-review', h, formatStatsDetails(circuitStats), createdAt);
  }

  function destroy(): void {
    destroyed = true;
  }

  return Object.freeze({
    name: 'ai-code-review',
    getDiff,
    getAutoComments,
    getSecurityFindings,
    getQualityScore,
    getRiskAssessment,
    addHumanComment,
    getAllComments,
    approve,
    requestChanges,
    getSummary,
    stats: getCircuitStats,
    health: getHealth,
    destroy,
  });
}
